/**
 * Advanced Execute Modal
 *
 * Lets the user pick an observability profile and/or custom per-family
 * observability overrides for a single Execute run, and persist that choice
 * as their default (via the IAM self-service user-settings API, key
 * "UserObserveSettings"). See processEngineExecution.types.ts for the
 * ObservabilityOverride shape this modal builds and TopToolbar.tsx's
 * handleExecute, which consumes it.
 *
 * Hand-coded (not Atlas-Forms schema-driven) — this is a small fixed field
 * set, not worth a generic FormRenderer. Uses the shared `Modal` support
 * component (fixed overlay + centered box + Esc-to-close + theme awareness),
 * the same shell `DownloadFlowEntityModal` next to this file already uses.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Modal } from '../Support/Modal';
import { useUIStore } from '@flow-studio/store';
import {
  observabilityProfileApiClient,
  userSettingsApiClient,
  type ObservabilityProfile,
  type ObservabilityOverride,
} from '@flow-studio/api';

const USER_OBSERVE_SETTINGS_KEY = 'UserObserveSettings';

type PayloadMaskLevel = 'None' | 'KeyNamesOnly' | 'Full';

type ObservabilityFamily =
  | 'Node'
  | 'Workflow'
  | 'ProgressReport'
  | 'Hil'
  | 'Remote'
  | 'IO'
  | 'Internal'
  | 'Performance'
  | 'Debug';

const ALL_FAMILIES: ObservabilityFamily[] = [
  'Node',
  'Workflow',
  'ProgressReport',
  'Hil',
  'Remote',
  'IO',
  'Internal',
  'Performance',
  'Debug',
];

/**
 * Local shape of observabilityConfigJson (see processEngineExecution.types.ts
 * ObservabilityOverride doc comment — field names are exact, do not rename).
 */
interface ObservabilityConfig {
  enableCustomSettings: true;
  defaultMaskLevel?: PayloadMaskLevel;
  slowThresholdMs?: number;
  families: Partial<Record<ObservabilityFamily, { enabled: boolean }>>;
}

const DEFAULT_MASK_LEVEL: PayloadMaskLevel = 'Full';
const DEFAULT_SLOW_THRESHOLD_MS = 5000;

const defaultFamilyState = (): Record<ObservabilityFamily, boolean> => {
  const state = {} as Record<ObservabilityFamily, boolean>;
  ALL_FAMILIES.forEach((f) => { state[f] = true; });
  return state;
};

// "Node" here means the node LIFECYCLE checkpoints specifically (Starting/Entry/Process/
// Completed/Error) -- not "everything a node does". HIL/Remote/IO/Internal/Performance/Debug
// are sibling categories a node can ALSO emit while running (an HTTP call -> Remote, a DB
// write -> IO, ...), not children of Node. Labelled "Node Lifecycle" to make that explicit
// after Binoy found the bare "Node" label misleading (2026-09-24).
const FAMILY_LABELS: Record<ObservabilityFamily, string> = {
  Node: 'Node Lifecycle',
  Workflow: 'Workflow',
  ProgressReport: 'Progress Report',
  Hil: 'HIL (Human-in-the-Loop)',
  Remote: 'Remote',
  IO: 'I/O',
  Internal: 'Internal',
  Performance: 'Performance',
  Debug: 'Debug',
};

export interface AdvancedExecuteModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the built override; caller is responsible for invoking handleExecute(override) and closing. */
  onExecute: (override: ObservabilityOverride) => void;
}

export const AdvancedExecuteModal: React.FC<AdvancedExecuteModalProps> = ({
  isOpen,
  onClose,
  onExecute,
}) => {
  const { theme, showToast } = useUIStore();

  const [profiles, setProfiles] = useState<ObservabilityProfile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [executing, setExecuting] = useState(false);

  const [selectedProfileID, setSelectedProfileID] = useState<number | ''>('');
  const [useCustomSettings, setUseCustomSettings] = useState(false);
  const [maskLevel, setMaskLevel] = useState<PayloadMaskLevel>(DEFAULT_MASK_LEVEL);
  const [slowThresholdMs, setSlowThresholdMs] = useState<number>(DEFAULT_SLOW_THRESHOLD_MS);
  const [families, setFamilies] = useState<Record<ObservabilityFamily, boolean>>(defaultFamilyState());

  // Baseline snapshot of what was actually loaded as the saved default, so we only
  // write back to (B)'s "set" endpoint when the form has actually changed — avoids
  // spamming an upsert on every Execute click when the user made no changes.
  const [isDirty, setIsDirty] = useState(false);

  const resetToBuiltInDefaults = useCallback(() => {
    setSelectedProfileID('');
    setUseCustomSettings(false);
    setMaskLevel(DEFAULT_MASK_LEVEL);
    setSlowThresholdMs(DEFAULT_SLOW_THRESHOLD_MS);
    setFamilies(defaultFamilyState());
    setIsDirty(false);
  }, []);

  // Load profile list + the user's saved default whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;

    resetToBuiltInDefaults();

    let cancelled = false;

    setLoadingProfiles(true);
    observabilityProfileApiClient
      .list({})
      .then((response) => {
        if (cancelled) return;
        if (response.success && Array.isArray(response.data)) {
          setProfiles(response.data);
        } else {
          setProfiles([]);
          if (response.error) {
            showToast('error', `Failed to load observability profiles: ${response.error.message}`);
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setProfiles([]);
        showToast('error', `Failed to load observability profiles: ${err?.message || 'Unknown error'}`);
      })
      .finally(() => {
        if (!cancelled) setLoadingProfiles(false);
      });

    setLoadingDefaults(true);
    userSettingsApiClient
      .getByKey(USER_OBSERVE_SETTINGS_KEY)
      .then((setting) => {
        if (cancelled || !setting?.settingValue) return;

        try {
          const saved: ObservabilityOverride = JSON.parse(setting.settingValue);

          if (saved.observabilityProfileID != null) {
            setSelectedProfileID(saved.observabilityProfileID);
          }

          if (saved.observabilityConfigJson) {
            const config: ObservabilityConfig = JSON.parse(saved.observabilityConfigJson);
            setUseCustomSettings(!!config.enableCustomSettings);
            if (config.defaultMaskLevel) setMaskLevel(config.defaultMaskLevel);
            if (config.slowThresholdMs != null) setSlowThresholdMs(config.slowThresholdMs);
            if (config.families) {
              setFamilies((prev) => {
                const next = { ...prev };
                (Object.keys(config.families) as ObservabilityFamily[]).forEach((key) => {
                  const entry = config.families[key];
                  if (entry) next[key] = !!entry.enabled;
                });
                return next;
              });
            }
          }
        } catch (parseErr) {
          console.error('[AdvancedExecuteModal] Failed to parse saved UserObserveSettings:', parseErr);
        }
      })
      .catch((err) => {
        // Not-found / no default saved yet is the expected common case — do not toast an error for it.
        console.warn('[AdvancedExecuteModal] No saved observability default (or lookup failed):', err?.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingDefaults(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const markDirty = useCallback(() => setIsDirty(true), []);

  const handleProfileChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    setSelectedProfileID(raw === '' ? '' : Number(raw));
    markDirty();
  };

  const handleToggleFamily = (family: ObservabilityFamily) => {
    setFamilies((prev) => ({ ...prev, [family]: !prev[family] }));
    markDirty();
  };

  /** Builds the ObservabilityOverride object from current form state, per the spec's contract:
   *  omit fields the user didn't actively set (e.g. no observabilityProfileID key at all when
   *  the profile dropdown is left on "no override"). */
  const buildOverride = useCallback((): ObservabilityOverride => {
    const override: ObservabilityOverride = {};

    if (selectedProfileID !== '') {
      override.observabilityProfileID = selectedProfileID;
    }

    if (useCustomSettings) {
      const config: ObservabilityConfig = {
        enableCustomSettings: true,
        defaultMaskLevel: maskLevel,
        slowThresholdMs,
        families: ALL_FAMILIES.reduce((acc, family) => {
          acc[family] = { enabled: families[family] };
          return acc;
        }, {} as Record<ObservabilityFamily, { enabled: boolean }>),
      };
      override.observabilityConfigJson = JSON.stringify(config);
    }

    return override;
  }, [selectedProfileID, useCustomSettings, maskLevel, slowThresholdMs, families]);

  const persistAsDefault = useCallback(async (override: ObservabilityOverride) => {
    const response = await userSettingsApiClient.set(USER_OBSERVE_SETTINGS_KEY, JSON.stringify(override));
    if (!response.success && response.error) {
      throw new Error(response.error.message);
    }
  }, []);

  const handleCancel = () => {
    onClose();
  };

  const handleExecuteClick = async () => {
    if (executing) return;
    setExecuting(true);
    try {
      const override = buildOverride();

      if (isDirty) {
        try {
          await persistAsDefault(override);
        } catch (err: any) {
          // Saving the default failing should not block the run itself — surface it and continue.
          showToast('error', `Could not save this as your default: ${err?.message || 'Unknown error'}`);
        }
      }

      onExecute(override);
      onClose();
    } finally {
      setExecuting(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 600,
    color: theme === 'dark' ? '#cbd5e1' : '#374151',
    display: 'block',
    marginBottom: '6px',
  };

  const helpTextStyle: React.CSSProperties = {
    fontSize: '12px',
    color: theme === 'dark' ? '#94a3b8' : '#6b7280',
    marginTop: '4px',
  };

  const fieldWrapperStyle: React.CSSProperties = { marginBottom: '18px' };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: '6px',
    border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
    background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff',
    color: theme === 'dark' ? '#e5e7eb' : '#111827',
    fontSize: '13px',
    boxSizing: 'border-box',
  };

  const sectionDividerStyle: React.CSSProperties = {
    height: '1px',
    background: theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    margin: '18px 0',
  };

  const familyGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px 16px',
  };

  const familyItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: theme === 'dark' ? '#e5e7eb' : '#111827',
    cursor: 'pointer',
  };

  const selectedProfile = profiles.find((p) => p.observabilityProfileID === selectedProfileID);
  const isDevelopmentProfileSelected = !!selectedProfile && /development/i.test(selectedProfile.profileName);

  const secondaryButtonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '6px',
    border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
    background: 'transparent',
    color: theme === 'dark' ? '#cbd5e1' : '#374151',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
  };

  const primaryButtonStyle: React.CSSProperties = {
    padding: '8px 20px',
    borderRadius: '6px',
    border: 'none',
    background: executing ? '#666' : (theme === 'dark' ? '#3a8c45' : '#10b981'),
    color: '#fff',
    cursor: executing ? 'wait' : 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    opacity: executing ? 0.75 : 1,
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Advanced Execute"
      maxWidth="560px"
      footer={
        <>
          <button style={secondaryButtonStyle} onClick={handleCancel} disabled={executing}>
            Cancel
          </button>
          <button style={primaryButtonStyle} onClick={handleExecuteClick} disabled={executing || loadingProfiles || loadingDefaults}>
            {executing ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                Executing...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Execute
              </>
            )}
          </button>
        </>
      }
    >
      <p style={{ ...helpTextStyle, marginTop: 0, marginBottom: '18px' }}>
        Pick an observability profile and/or custom settings for this one run. Your choice is saved as your
        default for next time.
      </p>

      {/* Profile picker */}
      <div style={fieldWrapperStyle}>
        <label style={labelStyle} htmlFor="advanced-execute-profile">Observability Profile</label>
        <select
          id="advanced-execute-profile"
          style={inputStyle}
          value={selectedProfileID}
          onChange={handleProfileChange}
          disabled={loadingProfiles}
        >
          <option value="">
            {loadingProfiles ? 'Loading profiles...' : '(Use current default / no profile override)'}
          </option>
          {profiles.map((p) => (
            <option key={p.observabilityProfileID} value={p.observabilityProfileID}>
              {p.profileName}{p.isBuiltIn ? ' (built-in)' : ''}
            </option>
          ))}
        </select>
        {isDevelopmentProfileSelected && (
          <div style={{ ...helpTextStyle, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Development profile logs unmasked data — for local debugging only. Do not use against a real tenant.
          </div>
        )}
      </div>

      <div style={sectionDividerStyle} />

      {/* Custom settings toggle */}
      <div style={fieldWrapperStyle}>
        <label style={{ ...familyItemStyle, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={useCustomSettings}
            onChange={(e) => { setUseCustomSettings(e.target.checked); markDirty(); }}
          />
          Use custom observability settings for this run
        </label>
      </div>

      {useCustomSettings && (
        <>
          <div style={fieldWrapperStyle}>
            <label style={labelStyle} htmlFor="advanced-execute-mask-level">Payload Masking</label>
            <select
              id="advanced-execute-mask-level"
              style={inputStyle}
              value={maskLevel}
              onChange={(e) => { setMaskLevel(e.target.value as PayloadMaskLevel); markDirty(); }}
            >
              <option value="None">None</option>
              <option value="KeyNamesOnly">Key Names Only</option>
              <option value="Full">Full</option>
            </select>
          </div>

          <div style={fieldWrapperStyle}>
            <label style={labelStyle} htmlFor="advanced-execute-slow-threshold">Slow Threshold (ms)</label>
            <input
              id="advanced-execute-slow-threshold"
              type="number"
              min={0}
              step={100}
              style={inputStyle}
              value={slowThresholdMs}
              onChange={(e) => { setSlowThresholdMs(Number(e.target.value) || 0); markDirty(); }}
            />
          </div>

          <div style={fieldWrapperStyle}>
            <label style={labelStyle}>Event Families</label>
            <div style={familyGridStyle}>
              {ALL_FAMILIES.map((family) => (
                <label key={family} style={familyItemStyle}>
                  <input
                    type="checkbox"
                    checked={families[family]}
                    onChange={() => handleToggleFamily(family)}
                  />
                  {FAMILY_LABELS[family]}
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};
